// Market periods for the schedule builder (#609): the catalog's period
// vocabulary (`filter_options.periods`), one market row per market key and
// period ("Passing Yards", "Passing Yards · 1st half"), and the saved shape.
//
// A schedule stores its picks as `filters.market_keys` (unique keys) plus
// `filters.market_periods` (`{ key: [period ids] }`). A key without an entry
// follows `filters.periods`, as the server reads it, and without those its
// default (the full game, or a part-game key's own period): a schedule saved
// before periods reads as full-game picks. Such keys get an entry only once
// the operator changes their picks (`touchPairs`), so an unchanged schedule
// saves as it was stored. `filters.periods: ["any"]` lets markets without a
// period of their own (families, or any market) use every period.
//
// Without a vocabulary (an older server) every key has its full-game row
// only, and the builder writes neither `market_periods` nor `periods`
// (`pairsToFilters(pairs, { periods: false })`): an older server refuses
// fields it does not know.
import { canonicalSport } from '../prepackRecipeModel'
import { keySet, searchFields, stringList, valueKey } from './marketInventory'

const UNDERSCORE_PATTERN = /_/g
const FIRST_CHARACTER_PATTERN = /^./

export const FULL_GAME = 'full_game'
export const ANY_PERIOD = 'any'
/** The period filter's value for "All periods". */
export const ALL_PERIODS = 'all'

const EMPTY = []

/**
 * The catalog's periods in vocabulary order:
 * `[{ value, label, group, sports, index }]`, `sports` canonical and empty
 * for every sport. Invalid and repeated ids are dropped; [] without a list.
 */
export function normalizePeriods(options) {
  if (!Array.isArray(options)) return EMPTY
  const seen = new Set()
  const periods = []
  for (const option of options) {
    const value = valueKey(option?.value)
    if (!value || seen.has(value)) continue
    seen.add(value)
    const label = String(option.label || '').trim() || value
    periods.push({
      value,
      label,
      group: String(option.group || '').trim() || label,
      sports: stringList(option.sports).map(canonicalSport),
      index: periods.length,
    })
  }
  return periods
}

const valueList = (values) => (Array.isArray(values) ? values : EMPTY)

/** Whether a period exists in any of `sports` (a period without sports exists in all; no sports, every period). */
function inSports(period, sports) {
  if (sports.size === 0 || period.sports.length === 0) return true
  return period.sports.some((sport) => sports.has(sport))
}

/** The periods of the vocabulary that exist in the scoped sports (a Set of canonical sports; empty for any). */
export function periodsForSports(periods, sports) {
  return valueList(periods).filter((period) => inSports(period, sports))
}

/** The period groups ("Full game", "Halves", …) of a list of periods, once each, in order. */
export function periodGroups(periods) {
  return [...new Set(valueList(periods).map((period) => period.group))]
}

/** "1st half" for `half_1`: the vocabulary's label, else the id in words ("Any period" for `any`). */
export function periodLabel(period, byId) {
  const id = valueKey(period) || FULL_GAME
  if (id === ANY_PERIOD && !byId?.has(id)) return 'Any period'
  return byId?.get(id)?.label || id.replace(UNDERSCORE_PATTERN, ' ').replace(FIRST_CHARACTER_PATTERN, (letter) => letter.toUpperCase())
}

/** The id a pick is keyed by in Sets and Maps: `key|period`. */
export const pairKey = (key, period) => `${valueKey(key)}|${valueKey(period) || FULL_GAME}`

/**
 * A pick's name wherever it is listed alone (chips, summaries, messages):
 * the market's name for the full game, "Passing Yards · 1st half" otherwise.
 */
export function pairLabel(name, period, byId) {
  const id = valueKey(period) || FULL_GAME
  return id === FULL_GAME ? name : `${name} · ${periodLabel(id, byId)}`
}

/** The vocabulary by id, for labels. */
export const periodIndex = (periods) => new Map(valueList(periods).map((period) => [period.value, period]))

/**
 * The periods a market key is offered in for the scoped sports, in
 * vocabulary order: its `periods` that exist in its sports (the scoped ones
 * it is pinned to, or all scoped). Full game only without a vocabulary or
 * when the key lists no periods.
 */
export function keyPeriods(option, periods, sports = new Set()) {
  const vocabulary = valueList(periods)
  const own = stringList(option?.periods).map(valueKey)
  if (vocabulary.length === 0 || own.length === 0) return [FULL_GAME]
  const pinned = stringList(option?.sports).map(canonicalSport)
  const keySports = pinned.length === 0 ? sports
    : sports.size === 0 ? new Set(pinned)
      : new Set(pinned.filter((sport) => sports.has(sport)))
  // A key pinned only to sports outside the scope is still listed by an
  // impossible scope; it keeps its periods for the scope's sports.
  const effective = keySports.size > 0 ? keySports : sports
  const offered = new Set(own)
  return vocabulary
    .filter((period) => offered.has(period.value) && inSports(period, effective))
    .map((period) => period.value)
}

/**
 * One row per market key and period from normalized, sport-scoped market
 * options: the option's fields plus `period`, `periodLabel`, `periodGroup`,
 * `periodIndex`, `pairKey` and `displayLabel` (`pairLabel`). `label` stays
 * the market's name; search also reads the period's label when there is a
 * vocabulary. Rows of one key follow vocabulary order, full game first.
 */
export function expandMarketPairs(options, periods, sports = new Set()) {
  const vocabulary = valueList(periods)
  const byId = periodIndex(vocabulary)
  const rows = []
  for (const option of valueList(options)) {
    for (const period of keyPeriods(option, vocabulary, sports)) {
      const info = byId.get(period)
      const label = vocabulary.length > 0 ? periodLabel(period, byId) : ''
      rows.push({
        ...option,
        period,
        periodLabel: label,
        periodGroup: info?.group || '',
        periodIndex: info?.index ?? 0,
        pairKey: pairKey(option.key ?? option.value, period),
        displayLabel: pairLabel(option.label, period, byId),
        // The full game is every market's default: "game" should not find them all.
        ...(vocabulary.length > 0 && period !== FULL_GAME ? searchFields([option.label, option.value, label, ...valueList(option.aliases)]) : {}),
      })
    }
  }
  return rows
}

/**
 * The period a saved key without `market_periods` reads as: full game, or
 * the key's first period when the catalog does not offer it for the full
 * game (a first-inning market, say).
 */
function legacyPeriod(key, catalogPeriods) {
  const own = catalogPeriods.get(key)
  return own && own.length > 0 && !own.includes(FULL_GAME) ? own[0] : FULL_GAME
}

/** `{ key: [period ids] }` of a saved `market_periods`, keys and ids compared as `valueKey`. */
export function savedPeriodMap(value) {
  const map = new Map()
  if (!value || typeof value !== 'object' || Array.isArray(value)) return map
  for (const [key, ids] of Object.entries(value)) {
    const list = [...new Set(stringList(ids).map(valueKey))]
    if (list.length > 0) map.set(valueKey(key), list)
  }
  return map
}

/**
 * The saved picks in saved order: `[{ value, key, period, inherited? }]`
 * from `market_keys` and `market_periods`. A key without an entry follows
 * `filters.periods` as the server does ("any" alone when it holds "any"),
 * narrowed to the periods the catalog offers the key, and without those reads
 * as its default pick (see `legacyPeriod`). Those picks are `inherited`; one
 * in a stored period the key is not offered in (nothing else left) is also
 * `offered: false`. `catalogOptions` is the catalog's `market_keys`, raw or
 * normalized, and may be omitted.
 */
export function savedPairs(filters, catalogOptions = EMPTY) {
  const keys = valueList(filters?.market_keys)
  if (keys.length === 0) return EMPTY
  const saved = savedPeriodMap(filters?.market_periods)
  const stored = isAnyPeriod(filters) ? [ANY_PERIOD] : [...new Set(stringList(filters?.periods).map(valueKey))]
  const catalogPeriods = new Map(valueList(catalogOptions).map((option) => [valueKey(option?.value), stringList(option?.periods).map(valueKey)]))
  const seen = new Set()
  const pairs = []
  for (const value of keys) {
    const key = valueKey(value)
    if (!key || seen.has(key)) continue
    seen.add(key)
    const name = String(value).trim()
    const own = saved.get(key)
    if (own) {
      for (const period of own) pairs.push({ value: name, key, period })
      continue
    }
    if (stored.length === 0) {
      pairs.push({ value: name, key, period: legacyPeriod(key, catalogPeriods), inherited: true })
      continue
    }
    const offered = catalogPeriods.get(key) || EMPTY
    const narrowed = offered.length === 0 ? stored : stored.filter((period) => period === ANY_PERIOD || offered.includes(period))
    if (narrowed.length > 0) {
      for (const period of narrowed) pairs.push({ value: name, key, period, inherited: true })
    } else {
      // None of the stored periods is the key's: it shows, flagged, until changed.
      for (const period of stored) pairs.push({ value: name, key, period, inherited: true, offered: false })
    }
  }
  return pairs
}

/**
 * The picks after the operator changed `key`'s picks (every key when `key`
 * is omitted): that key's inherited picks become its own, so they are saved
 * as its `market_periods` entry, and the ones in a period it is not offered
 * in are dropped. A key with nothing but such picks stays inherited when
 * every key is touched (nothing it is offered in to save).
 */
export function touchPairs(pairs, key = null) {
  const list = valueList(pairs)
  const target = key == null ? null : valueKey(key)
  const keepInherited = new Set()
  if (target == null) {
    const usable = new Set(list.filter((pair) => pair.offered !== false).map((pair) => pair.key))
    for (const pair of list) if (!usable.has(pair.key)) keepInherited.add(pair.key)
  }
  const result = []
  for (const pair of list) {
    if ((target != null && pair.key !== target) || !pair.inherited || keepInherited.has(pair.key)) {
      result.push(pair)
      continue
    }
    if (pair.offered === false) continue
    result.push({ value: pair.value, key: pair.key, period: pair.period })
  }
  return result
}

/**
 * The picks without those `ids` (`pairKey`s), each key that lost a pick
 * touched (`touchPairs`), as a remove button writes them.
 */
export function withoutPairs(pairs, ids) {
  const removed = new Set(ids)
  const list = valueList(pairs)
  const isRemoved = (pair) => removed.has(pairKey(pair.key, pair.period))
  let kept = list.filter((pair) => !isRemoved(pair))
  for (const key of new Set(list.filter(isRemoved).map((pair) => pair.key))) kept = touchPairs(kept, key)
  return kept
}

/**
 * What a sport scope keeps, for `outOfScope`: the `valueKey`s of the
 * sport-scoped market options (`scopeMarkets`) and the `pairKey`s of their
 * rows (`expandMarketPairs`).
 */
export const scopeIndex = (scoped, rows) => ({
  keys: keySet(valueList(scoped).map((option) => option.value)),
  pairs: new Set(valueList(rows).map((row) => row.pairKey)),
})

/**
 * Whether a saved pick falls outside a scope (`scopeIndex`): its market, or
 * its period in the scope's sports. A pick in any period has no row of its
 * own; its market decides.
 */
export function outOfScope(pair, index) {
  return !index.keys.has(pair.key) || (pair.period !== ANY_PERIOD && !index.pairs.has(pairKey(pair.key, pair.period)))
}

/**
 * The filters a list of picks is saved as: `market_keys` (unique, lowercase,
 * first-seen order) and `market_periods` for the keys with picks of their
 * own (inherited picks write no entry; undefined when none has one). With
 * `periods: false` (a server without the period vocabulary) only
 * `market_keys`, so the stored `market_periods` is left as it was.
 */
export function pairsToFilters(pairs, { periods: withPeriods = true } = {}) {
  const keys = []
  const periods = new Map()
  for (const pair of valueList(pairs)) {
    const key = valueKey(pair?.key ?? pair?.value)
    if (!key) continue
    if (!keys.includes(key)) keys.push(key)
    if (pair.inherited || pair.offered === false) continue
    const period = valueKey(pair.period) || FULL_GAME
    const list = periods.get(key) || []
    if (!list.includes(period)) list.push(period)
    periods.set(key, list)
  }
  if (!withPeriods) return { market_keys: keys }
  return {
    market_keys: keys,
    market_periods: periods.size > 0 ? Object.fromEntries(periods) : undefined,
  }
}

/** Whether the schedule lets markets without a period of their own use every period. */
export function isAnyPeriod(filters) {
  return valueList(filters?.periods).some((value) => valueKey(value) === ANY_PERIOD)
}

/**
 * Labels for saved picks out of the raw catalog (`filter_options.market_keys`
 * and `.periods`): `[{ ...pair, name, label }]`, `label` as `pairLabel`.
 */
export function labelPairs(pairs, filterOptions) {
  const names = new Map(valueList(filterOptions?.market_keys).map((option) => [valueKey(option?.value), String(option?.label || '').trim()]))
  const byId = periodIndex(normalizePeriods(filterOptions?.periods))
  return valueList(pairs).map((pair) => {
    const name = names.get(pair.key) || String(pair.value)
    return { ...pair, name, label: pairLabel(name, pair.period, byId) }
  })
}

// ---------- Sport-specific pack types ----------
//
// The parlay engine's sport-specific pack recipes (catalog strategies with
// `kind: "sports"`) build only from full-game team and game markets: off the
// full game such a market has no pack market of its own there. The server
// refuses to save a game market picked in another period on those pack types
// (rules.validateSportsPathPeriods); these mirror it. Player props keep every
// period, "any" stays allowed (game markets then stay full game), and so does
// a part-game market in one of its own periods.

/** Whether a pack type is sport-specific (built by the engine's sports recipes; the server's `IsSportsStrategy`). */
export const isSportSpecific = (descriptor) => descriptor?.kind === 'sports'

/**
 * Whether a catalog market (raw or normalized) is a team or game market
 * rather than a player prop: its participant type is not "player" (the
 * server's `IsGameMarketKey`). Unknown without the catalog's metadata.
 */
export function isGameMarket(option) {
  const type = option?.participant_type ?? option?.participantType
  return typeof type === 'string' && type.trim() !== '' && valueKey(type) !== 'player'
}

/** Why a row is closed to a sport-specific pack type (the "Not supported" note). */
export const SPORT_SPECIFIC_REASON = 'sport_specific'

/** The save error for one pick, as the server words it. */
export const sportSpecificPeriodMessage = (label) => `"${label}" can't be used by sport-specific pack types; pick a generic pack type or the full-game market`

/**
 * Whether a market is itself a part-game one (its catalog periods leave out
 * the full game, as first inning total runs does) offered in `period`: it
 * keeps its key there (the server's `keyOwnsPeriod`).
 */
export function ownsPeriod(option, period) {
  const own = stringList(option?.periods).map(valueKey)
  return !own.includes(FULL_GAME) && own.includes(valueKey(period))
}

// The families whose markets belong to the contest, not a player (sportsmarket.IsGameFamily).
const GAME_FAMILIES = new Set(['team_moneyline', 'team_spread', 'game_total', 'team_total', 'game_prop', 'segment'])
export const SEGMENT_FAMILY = 'segment'

/**
 * The families a pick counts as, the way the engine reads a leg
 * (periodFamilyAllowed in prepacks/periods.go): its key's catalog family
 * and, off the full game, a game market's is also "segment", what Search
 * calls a part-game market (a 1st-half spread). Either satisfies an
 * included list and either being excluded excludes the pick. None for a key
 * the catalog gives no family, which fails an included list.
 */
export function pickFamilies(option, period) {
  const family = valueKey(option?.market_family)
  if (!family) return []
  const offFullGame = Boolean(period) && valueKey(period) !== FULL_GAME && valueKey(period) !== ANY_PERIOD
  return offFullGame && GAME_FAMILIES.has(family) && family !== SEGMENT_FAMILY ? [family, SEGMENT_FAMILY] : [family]
}

/**
 * Whether a market in `period` can't be used by the pack type: a game market
 * off the full game on a sport-specific one, unless the period is one of a
 * part-game market's own (NRFI/YRFI's 1st inning stays allowed).
 */
export function closedToPackType(option, period, descriptor) {
  const id = valueKey(period) || FULL_GAME
  return isSportSpecific(descriptor) && id !== FULL_GAME && id !== ANY_PERIOD && isGameMarket(option) && !ownsPeriod(option, id)
}

/**
 * The picks a sport-specific pack type can't use, as the server reads the
 * saved filters: each game market's `market_periods` entry, else
 * `filters.periods`, without the full game and "any"; a key with neither
 * keeps its own default and is not a pick of a period. `[{ value, key,
 * period, name, label }]` in saved order; [] for other pack types or without
 * the catalog's `market_keys`.
 */
export function sportSpecificPeriodPicks(filters, filterOptions, descriptor) {
  if (!isSportSpecific(descriptor)) return EMPTY
  const catalog = new Map(valueList(filterOptions?.market_keys).map((option) => [valueKey(option?.value), option]))
  if (catalog.size === 0) return EMPTY
  const saved = savedPeriodMap(filters?.market_periods)
  const stored = [...new Set(stringList(filters?.periods).map(valueKey))]
  const picks = []
  const seen = new Set()
  for (const value of valueList(filters?.market_keys)) {
    const key = valueKey(value)
    if (!key || seen.has(key)) continue
    seen.add(key)
    const option = catalog.get(key)
    for (const period of saved.get(key) || stored) {
      if (closedToPackType(option, period, descriptor)) picks.push({ value: String(value).trim(), key, period })
    }
  }
  return labelPairs(picks, filterOptions)
}
